/**
 * Overview store - S-DASH for ONE selected TPIN (RW-01 S1, S5, S6; ADR-005 v1.6
 * section 3.2 / 3.3; TL packet 3.2 and the Q1 ruling).
 *
 * Two independent surfaces share one scope (tin + from + to):
 *  - the combined stats (`/summary`): complete, partial (503, no totals) or error;
 *  - the branch list (`/branches`): first page, then "Load more" by cursor.
 *
 * Rules this store keeps:
 *  - scope change = fresh load; the cursor is never carried across scopes (3.2 rule 3);
 *  - `refresh` is the only re-read and it is an explicit user action: it re-queries
 *    THIS scope only, keeps the visible data under an explicit `refreshing` marker,
 *    and resets the list to page 1. No timer, no polling, no focus/visibility
 *    re-fetch anywhere (PD-33);
 *  - `hasMore === false` is the only stop signal; a 400 on an advanced cursor is
 *    the published past-end terminal, never an error banner (3.2 rule 2);
 *  - a superseded request is aborted and an answer whose request was aborted is
 *    discarded (R10), so an old scope can never read as current;
 *  - nothing is summed here: every count is a server value (D-12, R3/R4).
 *
 * Re-opening the SAME scope with settled data is a no-op: coming back from a
 * branch keeps the loaded pages and the scroll position (One-Window Drilldown).
 * S-BRANCH shares this store and reads only the branch list (TL ruling Q1).
 */

import { create } from 'zustand'
import { fetchBranchAdvance, fetchBranchFirstPage, fetchTpinSummary } from '@api/tpinApi'
import type {
  BranchesView,
  MoreState,
  ResolvedPeriod,
  SummaryView,
  TpinBranchPage,
  TpinScope,
} from '@domain/tpin'

interface OverviewState {
  /** The scope the views belong to; null before the first `open`. */
  readonly scope: TpinScope | null
  readonly summary: SummaryView
  readonly branches: BranchesView
  /** Select a TPIN and period: load both surfaces, or keep them when already settled. */
  open: (tin: string, period: ResolvedPeriod) => void
  /** S-BRANCH: select a TPIN and period for the branch lookup; reads ONLY the branch list. */
  openBranches: (tin: string, period: ResolvedPeriod) => void
  /** Explicit refresh of the selected scope (summary + branches, back to page 1). */
  refresh: () => void
  /** S-BRANCH explicit refresh: re-queries this TPIN's branch list only. */
  refreshBranches: () => void
  /** Retry only the summary after its error. */
  retrySummary: () => void
  /** Retry only the branch list after its error. */
  retryBranches: () => void
  /** Append the next branch page, or retry a failed one. */
  loadMore: () => void
  /** Abort whatever is in flight and settle every marker; later answers are discarded. */
  cancel: () => void
}

/**
 * One request in flight. Starting a new one aborts the one it replaces, and an
 * aborted signal marks an answer as stale (R10). Slots are objects, not names,
 * so two surfaces can never share or collide on a key.
 */
class InFlight {
  private current: AbortController | null = null

  begin(): AbortSignal {
    this.current?.abort()
    this.current = new AbortController()
    return this.current.signal
  }

  abort(): void {
    this.current?.abort()
  }
}

const summarySlot = new InFlight()
const branchesSlot = new InFlight()
const moreSlot = new InFlight()

function abortAll(): void {
  summarySlot.abort()
  branchesSlot.abort()
  moreSlot.abort()
}
const IDLE_MORE: MoreState = { kind: 'idle' }

function failure(what: string, error: unknown): string {
  const detail = error instanceof Error ? error.message : 'unknown error'
  return `Couldn't load ${what} - ${detail}`
}

/** Data kept visible under the refreshing marker, or null when there is nothing to keep. */
function holdSummary(view: SummaryView): SummaryView | null {
  if (view.kind === 'ready') return { ...view, refreshing: true }
  if (view.kind === 'partial') return { kind: 'partial', refreshing: true }
  return null
}

function holdBranches(view: BranchesView): BranchesView | null {
  return view.kind === 'ready' ? { ...view, refreshing: true, more: IDLE_MORE } : null
}

function settleSummary(view: SummaryView): SummaryView {
  if (view.kind === 'ready') return { ...view, refreshing: false }
  if (view.kind === 'partial') return { kind: 'partial', refreshing: false }
  return view
}

function settleBranches(view: BranchesView): BranchesView {
  if (view.kind !== 'ready') return view
  return { ...view, refreshing: false, more: view.more.kind === 'loading' ? IDLE_MORE : view.more }
}

function firstList(scope: TpinScope, page: TpinBranchPage): BranchesView {
  return {
    kind: 'ready',
    list: { scope, rows: page.branches, nextCursor: page.nextCursor, hasMore: page.hasMore },
    refreshing: false,
    more: IDLE_MORE,
  }
}

function sameScope(a: TpinScope, b: TpinScope): boolean {
  return a.tin === b.tin && a.from === b.from && a.to === b.to
}

function summarySettled(view: SummaryView): boolean {
  return view.kind === 'ready' || view.kind === 'partial'
}

export const useTpinOverviewStore = create<OverviewState>((set, get) => {
  async function loadSummary(scope: TpinScope, keepVisible: boolean): Promise<void> {
    const signal = summarySlot.begin()
    set((state) => ({ summary: (keepVisible ? holdSummary(state.summary) : null) ?? { kind: 'loading' } }))
    try {
      const outcome = await fetchTpinSummary(scope.tin, scope, signal)
      if (signal.aborted) return
      set({
        summary:
          outcome.kind === 'complete'
            ? { kind: 'ready', summary: outcome.summary, refreshing: false }
            : { kind: 'partial', refreshing: false },
      })
    } catch (error) {
      if (signal.aborted) return
      set({ summary: { kind: 'error', message: failure('the summary', error) } })
    }
  }

  async function loadBranches(scope: TpinScope, keepVisible: boolean): Promise<void> {
    moreSlot.abort()
    const signal = branchesSlot.begin()
    set((state) => ({ branches: (keepVisible ? holdBranches(state.branches) : null) ?? { kind: 'loading' } }))
    try {
      const page = await fetchBranchFirstPage(scope.tin, scope, signal)
      if (signal.aborted) return
      set({ branches: firstList(scope, page) })
    } catch (error) {
      if (signal.aborted) return
      set({ branches: { kind: 'error', message: failure('the branches', error) } })
    }
  }

  function withScope(action: (scope: TpinScope) => void): void {
    const { scope } = get()
    if (scope !== null) action(scope)
  }

  return {
    scope: null,
    summary: { kind: 'loading' },
    branches: { kind: 'loading' },

    open: (tin, period) => {
      const next: TpinScope = { tin, from: period.from, to: period.to }
      const { scope, summary, branches } = get()
      if (scope !== null && sameScope(scope, next) && summarySettled(summary) && branches.kind === 'ready') return
      abortAll()
      set({ scope: next })
      void loadSummary(next, false)
      void loadBranches(next, false)
    },

    openBranches: (tin, period) => {
      const next: TpinScope = { tin, from: period.from, to: period.to }
      const { scope, branches } = get()
      const same = scope !== null && sameScope(scope, next)
      if (same && branches.kind === 'ready') return
      abortAll()
      // A new scope drops the old summary; the same scope keeps whatever it holds.
      if (!same) set({ scope: next, summary: { kind: 'loading' } })
      void loadBranches(next, false)
    },

    refresh: () => {
      withScope((scope) => {
        void loadSummary(scope, true)
        void loadBranches(scope, true)
      })
    },

    refreshBranches: () => {
      withScope((scope) => {
        void loadBranches(scope, true)
      })
    },

    retrySummary: () => {
      withScope((scope) => {
        void loadSummary(scope, false)
      })
    },

    retryBranches: () => {
      withScope((scope) => {
        void loadBranches(scope, false)
      })
    },

    loadMore: () => {
      const { branches } = get()
      if (branches.kind !== 'ready' || branches.refreshing || branches.more.kind === 'loading') return
      const { list } = branches
      if (list.nextCursor === null) return
      const signal = moreSlot.begin()
      set({ branches: { ...branches, more: { kind: 'loading' } } })
      void (async () => {
        try {
          const outcome = await fetchBranchAdvance(list.scope.tin, list.scope, list.nextCursor as string, signal)
          if (signal.aborted) return
          if (outcome.kind === 'past-end') {
            // Published section 15: a 400 on an advanced cursor is the end of the list.
            set({ branches: { ...branches, list: { ...list, nextCursor: null, hasMore: false }, more: IDLE_MORE } })
            return
          }
          const { page } = outcome
          set({
            branches: {
              ...branches,
              list: {
                scope: list.scope,
                rows: [...list.rows, ...page.branches],
                nextCursor: page.nextCursor,
                hasMore: page.hasMore,
              },
              more: IDLE_MORE,
            },
          })
        } catch (error) {
          if (signal.aborted) return
          set({ branches: { ...branches, more: { kind: 'error', message: failure('more branches', error) } } })
        }
      })()
    },

    cancel: () => {
      abortAll()
      set((state) => ({ summary: settleSummary(state.summary), branches: settleBranches(state.branches) }))
    },
  }
})
